import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { BanknotesIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import {
  getGraduationById,
  batchUpdateAttendance,
  refundNonAttendeeOverride
} from '../../../../services/crmApi';
import LoadingSpinner from '../../../Common/LoadingSpinner';
import {
  getGraduationStudentDisplayName,
  getGraduationStudentEmail,
  getGraduationStudentEnrollmentNumber
} from '../../../../utils/graduationStudentFilters';

const depositStatusColors = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  PARTIALLY_PAID: 'bg-orange-100 text-orange-800',
  PAID: 'bg-green-100 text-green-800',
  FORFEITED: 'bg-red-100 text-red-800',
  REFUNDED: 'bg-blue-100 text-blue-800',
  WAIVED: 'bg-purple-100 text-purple-800'
};

const ENTRY_CATEGORIES = ['GRADUATION_FEE', 'STUDENT_PASS'];

/** Mirrors Graduation.canStudentAttend on the server (the server stays the authority). */
const getEntryStatus = (graduation, student) => {
  const unpaidPass = (student.passes || []).find((p) => (
    ENTRY_CATEGORIES.includes(p.category) && p.kind === 'FEE' && p.optedIn !== false && p.amount > 0 && p.status === 'ASSIGNED'
  ));
  if (unpaidPass) return { allowed: false, label: `${unpaidPass.name} unpaid`, status: 'PENDING' };
  if (!graduation.deposit?.enabled) {
    const hasEntryFee = (student.passes || []).some((p) => ENTRY_CATEGORIES.includes(p.category) && p.kind === 'FEE' && p.optedIn !== false && p.amount > 0);
    return { allowed: true, label: hasEntryFee ? 'Fees settled' : 'No entry fee', status: null };
  }
  const status = student.deposit?.status || 'PENDING';
  return { allowed: ['PAID', 'REFUNDED', 'WAIVED'].includes(status), label: status, status };
};

const GraduationAttendanceCheckin = () => {
  const { id } = useParams();
  const [graduation, setGraduation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState('');
  const [problems, setProblems] = useState([]);

  useEffect(() => {
    fetchGraduation();
  }, [id]);

  const fetchGraduation = async () => {
    try {
      setLoading(true);
      const response = await getGraduationById(id);
      const data = response.data.data;
      setGraduation(data);
      const initial = {};
      (data.students || []).forEach((s) => {
        initial[String(s._id)] = !!s.attendance?.confirmed;
      });
      setAttendance(initial);
    } catch (error) {
      console.error('Error fetching graduation:', error);
      toast.error('Failed to load graduation');
    } finally {
      setLoading(false);
    }
  };

  const toggleAttendance = (rowId) => {
    setAttendance({ ...attendance, [rowId]: !attendance[rowId] });
  };

  const handleSubmit = async () => {
    const attendances = Object.entries(attendance).map(([rowId, attended]) => ({ rowId, attended }));
    try {
      setSubmitting(true);
      setProblems([]);
      const res = await batchUpdateAttendance(id, attendances);
      const result = res.data?.data || {};
      if (result.errors?.length) {
        // Attendance was saved, but some refunds / forfeitures / journals need attention
        setProblems(result.errors);
        toast.error(`Attendance saved, but ${result.errors.length} item(s) need attention (see below)`);
      } else {
        toast.success(res.data?.message || 'Attendance updated');
      }
      fetchGraduation();
    } catch (error) {
      const apiErrors = error.response?.data?.errors;
      if (Array.isArray(apiErrors) && apiErrors.length) {
        setProblems(apiErrors);
      }
      toast.error(error.response?.data?.message || 'Failed to update attendance');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefundOverride = async (student) => {
    const reason = window.prompt('Override reason:');
    if (!reason) return;
    try {
      await refundNonAttendeeOverride(id, student.studentId?._id || student.studentId, student.enrollmentId?._id || student.enrollmentId, reason);
      toast.success('Refund processed');
      fetchGraduation();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to process refund');
    }
  };

  if (loading) return <LoadingSpinner />;

  const filteredStudents = (graduation.students || []).filter((s) => (
    getGraduationStudentDisplayName(s).toLowerCase().includes(filter.toLowerCase())
  ));

  const attendedCount = Object.values(attendance).filter(Boolean).length;
  const totalCount = graduation.students?.length || 0;
  const hasFees = graduation.deposit?.enabled || (graduation.passTypes || []).some((p) => p.active !== false && p.price > 0);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Attendance Check-In</h1>
          <p className="text-gray-600">{graduation.name}</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">Attended</p>
          <p className="text-2xl font-bold text-indigo-600">{attendedCount} / {totalCount}</p>
        </div>
      </div>

      {problems.length > 0 && (
        <div className="mb-6 border border-amber-300 bg-amber-50 rounded-xl overflow-hidden">
          <div className="px-4 py-2 flex items-center gap-2 text-sm font-semibold text-amber-900 bg-amber-100">
            <ExclamationTriangleIcon className="w-5 h-5" /> {problems.length} item(s) need attention
          </div>
          <ul className="divide-y divide-amber-200 text-sm text-amber-900 max-h-56 overflow-y-auto">
            {problems.map((p, i) => (
              <li key={i} className="px-4 py-2">
                <span className="font-medium">{p.name || p.studentId || p.rowId || 'Student'}:</span> {p.message}
                {p.type && <span className="ml-2 text-[10px] uppercase px-1.5 py-0.5 rounded bg-amber-200">{String(p.type).replace('_', ' ')}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <input
          type="text"
          placeholder="Search students..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full sm:w-80 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto max-h-[60vh]">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Attended</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Student</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Enrollment</th>
                {hasFees && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Entry / payment</th>}
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredStudents.map((student) => {
                const rowId = String(student._id);
                const attended = attendance[rowId] || false;
                const entry = getEntryStatus(graduation, student);
                return (
                  <tr key={rowId} className={attended ? 'bg-green-50' : ''}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={attended}
                        disabled={!entry.allowed}
                        onChange={() => toggleAttendance(rowId)}
                        className="w-5 h-5 rounded text-indigo-600 focus:ring-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div>{getGraduationStudentDisplayName(student)}</div>
                      <div className="text-xs text-gray-500">{getGraduationStudentEmail(student)}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{getGraduationStudentEnrollmentNumber(student) || 'N/A'}</td>
                    {hasFees && (
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${entry.status ? (depositStatusColors[entry.status] || 'bg-gray-100') : 'bg-gray-100 text-gray-700'}`}>
                          {entry.label}
                        </span>
                        {!entry.allowed && <span className="ml-2 text-xs text-red-600">(Entry blocked)</span>}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      {!attended && graduation.deposit?.enabled && student.deposit?.status === 'PAID' && (
                        <button
                          type="button"
                          title="Refund Override"
                          aria-label="Refund Override"
                          onClick={() => handleRefundOverride(student)}
                          className="p-1.5 rounded transition-all text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                        >
                          <BanknotesIcon className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {submitting ? 'Saving...' : 'Save Attendance'}
        </button>
      </div>
    </div>
  );
};

export default GraduationAttendanceCheckin;
